import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Box, Dialog, Textarea, UserLabel } from '@scope/scope-one-ui/core';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { useDebouncedCallback } from 'use-debounce';
import { RatingStatuses, RatingType, RatingUser, SubscriptionRatingType } from '../types';
import { addRatingUsers, filterUsers, getRatingUsers } from '../services/rsrService';
import { Styled } from './styled';

type AddRatingUserProps = {
  ratingId: string;
  ratingType: RatingType;
  ratingName: string;
  open: boolean;
  onClose: (added: boolean) => void;
  subscriptionRatingType: SubscriptionRatingType;
};

export const AssignUser: FC<AddRatingUserProps> = ({
  open,
  onClose,
  ratingId,
  ratingType,
  ratingName,
  subscriptionRatingType,
}) => {
  const { error, success } = useToasts();
  const [selectedUser, setSelectedUser] = useState<RatingUser>();
  const [users, setUsers] = useState<RatingUser[]>();
  const [assignedUsers, setAssignedUsers] = useState<RatingUser[]>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setSelectedUser(undefined);
    setUsers(undefined);
    fetchAssignedUsers();
  }, [ratingId, open]);

  const fetchAssignedUsers = async () => {
    const response = await getRatingUsers(subscriptionRatingType, ratingId, 1, 100);
    setAssignedUsers(
      (response.data as RatingUser[]).filter((u) => u.ratingStatus === RatingStatuses.APPROVED),
    );
    setIsLoading(false);
  };

  const fetchUsers = async (enteredMail: string) => {
    if (enteredMail.length > 3) {
      const result = await filterUsers(enteredMail);
      setUsers(result.data);
    }
  };

  const debouncedFetchUsers = useDebouncedCallback(fetchUsers, 500);

  const addUser = async () => {
    if (selectedUser) {
      try {
        const result = await addRatingUsers(subscriptionRatingType, ratingId, ratingType, [
          selectedUser.userId,
        ]);
        if (result) {
          success({ title: '', content: `User assigned to ${ratingName} successfully` });
        }
      } catch (e: any) {
        error({ title: '', content: e.message });
      }
    }
    setSelectedUser(undefined);
    onClose(true);
  };

  const onSearch = (v: string | number) => {
    debouncedFetchUsers(v.toString());
    setSelectedUser(undefined);
  };

  const onSelect = (v?: string | number) => {
    setSelectedUser(users?.find((u) => u.email === v));
  };

  if (isLoading) return <></>;
  return (
    <Dialog
      title="Assign user"
      open={open}
      onClose={() => onClose(false)}
      size="small"
      confirmLabel="Invite"
      onConfirm={addUser}
      disabled={!selectedUser}
    >
      <Box px={2} py={3} gridGap={2}>
        <Autocomplete
          label="Email address"
          placeholder="Type email address of existing user"
          onSearch={onSearch}
          onSelect={onSelect}
          options={users?.map((u) => u.email)}
          selectedValue={selectedUser?.email ?? ''}
        />

        {selectedUser ? (
          <Box mt={2}>
            <Textarea label="Message" placeholder="Type your message here" max={500} height={25} />
          </Box>
        ) : (
          <Styled.UserListWrapper display="flex" flexDirection="column" gridGap={2} mt={4}>
            {assignedUsers
              ?.filter((u) => u.firstName && u.lastName && u.organisationName)
              .map((u) => (
                <UserLabel
                  firstName={u.firstName}
                  jobTitle={u.organisationName}
                  lastName={u.lastName}
                />
              ))}
          </Styled.UserListWrapper>
        )}
      </Box>
    </Dialog>
  );
};
